import React, { useState, useCallback } from 'react';

// --- Helper Icons (as SVG components) ---
const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 h-6 w-6">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const DownloadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 h-5 w-5">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);

const LoadingSpinner = () => (
    <div className="flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    </div>
);


export default function App() {
    const [uploadedImage, setUploadedImage] = useState(null);
    const [uploadedImagePreview, setUploadedImagePreview] = useState(null);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [prompt, setPrompt] = useState(
        "Create a professional ID photo (2 × 2 inch, 51 × 51 mm, passport size) with ultra-high resolution (8K quality). Use the uploaded face photo exactly as it is, without distortion. Ensure natural skin tone, sharp facial details, and realistic lighting. Background must be plain solid light blue, evenly lit, with no shadows or textures. Automatically change the clothing to a formal Western suit and tie (dark-colored suit, white shirt, neat tie), regardless of the original outfit in the uploaded photo. Crop the image to include face and shoulders properly, centered, with the correct head-to-frame ratio according to ID/passport standards. No extra retouching, no makeup enhancement, no artistic filters — just a natural, clean, official look. Output must be crystal clear, detailed, and ready for official ID/passport use without further editing."
    );

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Add a file size check (e.g., 10MB)
            if (file.size > 10 * 1024 * 1024) { 
                setError("ဖိုင်အရွယ်အစားကြီးလွန်းပါသည်။ 10MB အောက်ရှိသောပုံကို တင်ပေးပါ။");
                setUploadedImage(null);
                setUploadedImagePreview(null);
                return;
            }
            setUploadedImage(file);
            setGeneratedImage(null);
            setError(null);
            const reader = new FileReader();
            reader.onloadend = () => {
                setUploadedImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const generateImageWithRetry = useCallback(async (base64ImageData) => {
        const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-image-preview:generateContent?key=${apiKey}`;

        const payload = {
            contents: [{
                parts: [
                    { text: prompt },
                    {
                        inlineData: {
                            mimeType: "image/png",
                            data: base64ImageData
                        }
                    }
                ]
            }],
             generationConfig: {
                 responseModalities: ['TEXT', 'IMAGE']
             },
        };

        let attempts = 0;
        const maxAttempts = 5;
        const initialDelay = 1000;

        while (attempts < maxAttempts) {
            try {
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    const errorBody = await response.json();
                    throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorBody.error?.message || 'Unknown error'}`);
                }

                const result = await response.json();
                const base64Data = result?.candidates?.[0]?.content?.parts?.find(p => p.inlineData)?.inlineData?.data;
                
                if (base64Data) {
                    return `data:image/png;base64,${base64Data}`;
                } else {
                    // Provide more specific error messages based on the API response
                    const finishReason = result?.candidates?.[0]?.finishReason;
                    if (finishReason === 'SAFETY') {
                        throw new Error("ဘေးကင်းရေးစနစ်များကြောင့် ပုံထုတ်မရပါ။ အခြားဓာတ်ပုံဖြင့် ကြိုးစားကြည့်ပါ။");
                    }
                    throw new Error("AI မှ ဓာတ်ပုံပြန်မထုတ်ပေးပါ။ ထပ်မံကြိုးစားကြည့်ပါ သို့မဟုတ် အခြားဓာတ်ပုံသုံးပါ။");
                }

            } catch (err) {
                attempts++;
                if (attempts >= maxAttempts) {
                    throw err;
                }
                const delay = initialDelay * Math.pow(2, attempts);
                console.warn(`Attempt ${attempts} failed. Retrying in ${delay}ms...`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
         throw new Error("Failed to generate image after multiple attempts.");
    }, [prompt]);

    const handleGenerate = useCallback(async () => {
        if (!uploadedImage) {
            setError("Please upload an image first.");
            return;
        }

        setIsLoading(true);
        setError(null);
        setGeneratedImage(null);

        try {
            const reader = new FileReader();
            reader.readAsDataURL(uploadedImage);
            reader.onloadend = async () => {
                try {
                    const base64String = reader.result.split(',')[1];
                    const imageUrl = await generateImageWithRetry(base64String);
                    setGeneratedImage(imageUrl);
                } catch (err) {
                     console.error(err);
                    setError(err.message || "Failed to generate image. Please try again.");
                } finally {
                    setIsLoading(false);
                }
            };
            reader.onerror = () => {
                setError("Failed to read the uploaded file.");
                setIsLoading(false);
            };
        } catch (err) {
            setError("An unexpected error occurred.");
            setIsLoading(false);
        }
    }, [uploadedImage, generateImageWithRetry]);
    
    const handleDownload = () => {
        if (!generatedImage) return;
        const link = document.createElement('a');
        link.href = generatedImage;
        link.download = 'id_photo.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    return (
        <div className="bg-gray-100 min-h-screen font-sans text-gray-800">
            <div className="container mx-auto p-4 md:p-8">
                <header className="text-center mb-8">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900">တရားဝင် ID ဓာတ်ပုံ ဖန်တီးစနစ်</h1>
                    <p className="text-lg text-gray-600 mt-2">AI ဖြင့် တရားဝင် ဓာတ်ပုံများ ဖန်တီးပါ</p>
                </header>

                <main className="bg-white rounded-2xl shadow-lg p-6 md:p-10 max-w-5xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                        
                        {/* Left Column: Upload and Prompt */}
                        <div className="flex flex-col space-y-6">
                            <div>
                                <label htmlFor="prompt" className="block text-lg font-semibold mb-2 text-gray-700">ဓာတ်ပုံဖန်တီးရန် လမ်းညွှန်ချက်</label>
                                <textarea
                                    id="prompt"
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    rows="12"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 resize-none"
                                    placeholder="Enter your detailed prompt here..."
                                />
                            </div>

                             <div>
                                <h2 className="text-lg font-semibold mb-2 text-gray-700">၁။ သင်၏ ဓာတ်ပုံကို တင်ပါ</h2>
                                <label htmlFor="image-upload" className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition duration-200">
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6 text-gray-500">
                                        <UploadIcon />
                                        <p className="mb-2 text-sm"><span className="font-semibold">တင်ရန် နှိပ်ပါ</span> သို့မဟုတ် ဆွဲထည့်ပါ</p>
                                        <p className="text-xs">PNG, JPG, or WEBP</p>
                                    </div>
                                    <input id="image-upload" type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                </label>
                            </div>
                            
                            <button
                                onClick={handleGenerate}
                                disabled={isLoading || !uploadedImage}
                                className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 flex items-center justify-center text-lg"
                            >
                                {isLoading ? 'ဖန်တီးနေသည်...' : '၂။ ID ဓာတ်ပုံ ဖန်တီးပါ'}
                            </button>
                        </div>

                        {/* Right Column: Previews and Result */}
                        <div className="flex flex-col space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
                                <div className="flex flex-col items-center justify-center bg-gray-50 p-3 rounded-lg border">
                                    <h3 className="text-md font-semibold text-gray-600 mb-2">မူရင်း</h3>
                                    <div className="w-full h-48 flex items-center justify-center">
                                    {uploadedImagePreview ? (
                                        <img src={uploadedImagePreview} alt="Uploaded preview" className="max-h-full max-w-full object-contain rounded-md shadow-sm" />
                                    ) : (
                                        <div className="text-gray-400 text-sm px-2 text-center">နမူနာပုံကြည့်ရန် ဓာတ်ပုံတင်ပါ</div>
                                    )}
                                    </div>
                                </div>
                                <div className="flex flex-col items-center justify-center bg-gray-50 p-3 rounded-lg border">
                                    <h3 className="text-md font-semibold text-gray-600 mb-2">ဖန်တီးပြီး ID ဓာတ်ပုံ</h3>
                                     <div className="w-full h-48 flex items-center justify-center p-2">
                                        {isLoading && <LoadingSpinner />}
                                        {error && (
                                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm text-center w-full">
                                                <strong className="font-bold block">အမှားအယွင်း!</strong>
                                                <p className="block">{error}</p>
                                            </div>
                                        )}
                                        {generatedImage && !isLoading && (
                                            <img src={generatedImage} alt="Generated ID" className="max-h-full max-w-full object-contain rounded-md shadow-sm" />
                                        )}
                                        {!generatedImage && !isLoading && !error && (
                                            <div className="text-gray-400 text-sm px-2 text-center">ရလဒ်ကို ဤနေရာတွင် ပြသပါမည်</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                             {generatedImage && !isLoading && (
                                <div className="mt-auto pt-4">
                                     <button
                                        onClick={handleDownload}
                                        className="w-full bg-green-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-green-700 transition-all duration-300 transform hover:scale-105 flex items-center justify-center text-lg"
                                    >
                                        <DownloadIcon />
                                        ဓာတ်ပုံ ဒေါင်းလုဒ်ဆွဲရန်
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}

